import { DictionaryTypeEnum, SortingKindEnum } from '../../../utils/interfaces';
import sortItemByKind from '../../utils/sortItemByKind';
import {
  IDictionaryBasicEvent,
  IField,
  Order,
} from '../../../utils/apollo/graphqlSchema';
import { IDictionaryState } from './types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const defaultSortConfig = {
  // index is for column index which is the sorting key at the moment.
  index: 0,
  sortingType: Order.Asc,
  sortingKey: SortingKindEnum.NAME,
};

/**
 * generate different default sortingConfig by @param dictionaryType,
 * Each config have different sorting index and sorting key.
 * default sorting key is NAME, but for event and fields, using KEY since Key is on column index 0
 */
const generateDefaultSortingConfigsByDictionaryType = () => {
  const dictionaryTypeList = Object.values(DictionaryTypeEnum);
  const defaultSortConfigs: any = {};
  for (const dictionaryType of dictionaryTypeList) {
    if (
      dictionaryType === DictionaryTypeEnum.DictionaryEvent ||
      dictionaryType === DictionaryTypeEnum.DictionaryField
    ) {
      defaultSortConfigs[dictionaryType] = {
        ...defaultSortConfig,
        sortingKey: SortingKindEnum.KEY,
      };
    } else {
      defaultSortConfigs[dictionaryType] = { ...defaultSortConfig };
    }
  }

  return defaultSortConfigs;
};

const initialState: IDictionaryState = {
  field: [],
  filter: [],
  measure: [],
  event: [],
  virtualEvent: [],
  cohort: [],
  calendarIncident: [],
  scheduleCohort: [],
  buckets: [],
  users: [],
  roles: [],
  userGroups: [],
  sortingConfigs: generateDefaultSortingConfigsByDictionaryType(),

  selections: [], // used to determine which dictionary entity IDs are selected.
};

export default createSlice({
  name: 'dictionary',
  initialState,
  reducers: {
    setDictionaryListAction: (
      state,
      action: PayloadAction<{
        newValues: any;
        dictionaryType: DictionaryTypeEnum;
      }>,
    ) => {
      const { newValues, dictionaryType } = action.payload;
      const sortingConfig = state.sortingConfigs[dictionaryType];
      state[dictionaryType] = sortItemByKind(
        newValues,
        sortingConfig.sortingKey,
        sortingConfig.sortingType,
      );
    },

    setDictionarySortAction: (
      state,
      action: PayloadAction<{
        dictionaryType: any;
        value: any;
        index: any;
        sortKind: any;
      }>,
    ) => {
      const { dictionaryType, value, index, sortKind } = action.payload;

      const draftConfig = state.sortingConfigs[dictionaryType];
      const sortingList = state[dictionaryType];
      if (!draftConfig || !sortingList) {
        return;
      }
      draftConfig.index = index;
      draftConfig.sortingType = value;
      draftConfig.sortingKey = sortKind;
      sortItemByKind(sortingList, sortKind, value);
    },

    updateDictionaryItemAction: (
      state,
      action: PayloadAction<{ item: any; dictionaryType: DictionaryTypeEnum }>,
    ) => {
      const { item, dictionaryType } = action.payload;
      const itemList = state[dictionaryType];
      const targetIndex = itemList.findIndex((currItem: any) =>
        dictionaryType === DictionaryTypeEnum.DictionaryEvent ||
        dictionaryType === DictionaryTypeEnum.DictionaryField
          ? currItem.key === item.key
          : currItem.id === item.id,
      );
      if (targetIndex >= 0) {
        itemList[targetIndex] = { ...item };
      }
    },

    deleteDictionaryItemAction: (
      state,
      action: PayloadAction<{
        itemId: any;
        dictionaryType: any;
      }>,
    ) => {
      const { itemId, dictionaryType } = action.payload;
      const currList = state[dictionaryType];
      state[dictionaryType] = currList.filter(
        (currItem: any) => currItem.id !== itemId,
      );
    },

    toggleAllDictionarySelectionsAction: (
      state,
      action: PayloadAction<{
        dictionaryType: DictionaryTypeEnum;
      }>,
    ) => {
      const { dictionaryType } = action.payload;

      const dictionaryItems = state[dictionaryType];

      // if the length of selections are same as dictionaryItems
      // then toggle everything off
      if (state.selections.length === dictionaryItems.length) {
        state.selections = [];
        return;
      }

      if (
        dictionaryType === DictionaryTypeEnum.DictionaryEvent ||
        dictionaryType === DictionaryTypeEnum.DictionaryField
      ) {
        state.selections = dictionaryItems.map(
          (item: IDictionaryBasicEvent | IField) => item.key,
        );
      } else {
        state.selections = dictionaryItems.map((item: any) => item.id);
      }
    },
  },
});
